import { Platform } from "react-native";

// ─── Colour roles ────────────────────────────────────────────────────
//
// One copper accent and three status colours, decided on #3910. Words and
// icons use the role colour; the soft tint of each sits behind tags, chips
// and dots, at the lightness the soft palette's tints had.

const ACCENT = "#B84A16";
const DANGER = "#C8241E";
/** The blue-green. Switching to the leaf green #2E7D32 is this one value. */
const SUCCESS = "#187C53";
const WARNING = "#F0B400";
const WARNING_TEXT = "#896400";

/**
 * A colour at an opacity, as `rgba()`: the one way to draw a tint from a
 * token, in place of a two-digit hex suffix.
 */
export function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

// ─── Colors ──────────────────────────────────────────────────────────

export const colors = {
  /** Words, lines and icons in the accent: links, checks, the selected mark. */
  accent: ACCENT,
  /**
   * Accent fills (a selected chip, a slider track, the completion mark, a
   * progress fill), lit from the top left like metal. Draw it with
   * `gradients.accent`.
   */
  accentGradient: ["#D4692C", "#A83E0B"] as const,
  /** The accent's soft wash behind a tag or chip. */
  accentTint: withAlpha(ACCENT, 0.07),
  /** Errors, deletes and emergencies: words and icons. */
  danger: DANGER,
  dangerTint: withAlpha(DANGER, 0.09),
  /** Done, taken and positive: words, icons and done checks. */
  success: SUCCESS,
  successTint: withAlpha(SUCCESS, 0.07),
  /** Warning icons and fills: dose late, pending, the notice's triangle. */
  warning: WARNING,
  /** Warning words, darker than the yellow so they read on cream. */
  warningText: WARNING_TEXT,
  warningTint: withAlpha(WARNING, 0.16),

  // Core warm palette. The soft accents are retired for words, icons and
  // accents (#3910): their names now follow the roles.
  /** @deprecated The tan accent; now the copper accent. Use `accent`. */
  warm: ACCENT,
  warmDark: "#C4956A",
  warmLight: "#FFF8F0",
  /**
   * @deprecated Soft coral. Use `danger`. It keeps its value only for the
   * error rim on the dark sign-in glass, until that moves to the on-dark
   * danger (#3953).
   */
  coral: "#E8927C",
  teal: "#5B9E9E",
  blue: "#7B8EB5",
  /** @deprecated The mint; now the success green. Use `success`. */
  green: SUCCESS,
  /** @deprecated The butter; now the warning yellow. Use `warning`. */
  gold: WARNING,
  deepBrown: "#3B1A08",
  greyBrown: "#6B5548",

  // Biology illustration palette
  biologyCream: "#FFF8EC",
  biologyFrost: "#F1D1A6",
  biologyAmber: "#F4B34F",
  biologyCopper: "#B76545",
  biologyBrown: "#542F27",

  // Text hierarchy: the warm greys, the one text palette (#3880)
  text: "#2A2522",
  textMid: "#5A4D45",
  textLight: "#8A7D74",
  textFaint: "#B0A49A",

  // The soft brown greys text used before it moved to the warm ones. Only
  // marks that are not words keep them (dots, fills, tints, focus borders,
  // the skipped dose), until the colours audit (#3910) gives them roles.
  softBrown: "#4A3F35",
  softBrownMid: "#6B5E54",
  softBrownLight: "#8B7E72",
  softBrownFaint: "#B5ADA5",

  // Surfaces
  bg: "#FFF8F0",
  bgEnd: "#F5EDE4",
  border: "#F0EBE5",
  surface: "#FFFFFF",

  // Phase colors
  phases: {
    exploring: "#D4A574",
    active_cycle: "#E8927C",
    post_retrieval: "#F5C882",
    storage: "#7B8EB5",
  } as const,

  // Evidence strength
  evidence: {
    strong: "#82D4A8",
    moderate: "#F5C882",
    emerging: "#7B8EB5",
    mixed: "#E8927C",
  } as const,

  // Semantic
  /** @deprecated Use `danger`. */
  error: DANGER,
  info: "#7B8EB5",
} as const;

// ─── Semantic Colors ─────────────────────────────────────────────────

export const semanticColors = {
  background: {
    app: colors.bg,
    appEnd: colors.bgEnd,
  },
  surface: {
    default: colors.surface,
    raised: colors.surface,
    sheet: colors.surface,
    subtle: "rgba(255, 255, 255, 0.36)",
    glass: "rgba(255, 255, 255, 0.3)",
    muted: "rgba(42, 37, 34, 0.03)",
    input: "rgba(255, 255, 255, 0.6)",
    transparent: "transparent",
  },
  border: {
    default: colors.border,
    subtle: "rgba(42, 37, 34, 0.08)",
    strong: "rgba(42, 37, 34, 0.12)",
    glass: "rgba(255, 255, 255, 0.5)",
    // A focused field's border, everywhere: the form labels' grey as it was
    // before text moved to the warm greys.
    focus: colors.softBrownLight,
  },
  text: {
    primary: colors.text,
    secondary: colors.textMid,
    tertiary: colors.textLight,
    faint: colors.textFaint,
    inverse: colors.surface,
    /** Remove and delete actions, and errors: the danger red. */
    destructive: colors.danger,
    /** Navigation links such as "< Back": the copper accent. */
    link: colors.accent,
  },
  /** Role-based colors for the canonical ordinary title/subtitle pair. */
  typography: {
    primary: colors.text,
    supporting: colors.textLight,
  },
  /** The one copper accent (#3910). */
  accent: {
    /** Words, lines and icons: links, checks, the selected mark. */
    default: colors.accent,
    /**
     * Fills: a selected chip, a slider track, the completion mark, a
     * progress fill. `gradients.accent` adds the direction it is lit from.
     */
    gradient: colors.accentGradient,
    /** The soft wash behind an accent tag or chip, or a selected row. */
    tint: colors.accentTint,
  },
  action: {
    primary: {
      background: colors.deepBrown,
      foreground: colors.surface,
      pressedOverlay: "rgba(0, 0, 0, 0.06)",
    },
    /**
     * The muted action: a warm grey-brown tint with a grey-brown label. Used
     * by the full-size secondary, the compact primary and soft icon buttons,
     * in place of a neutral grey that read as disabled.
     */
    tonal: {
      background: "rgba(107,85,72,0.12)",
      pressedBackground: "rgba(107,85,72,0.18)",
      foreground: colors.greyBrown,
    },
    quiet: {
      background: "rgba(255,255,255,0.48)",
      foreground: colors.textMid,
      border: "rgba(92,52,32,0.08)",
      pressedBackground: "rgba(255,255,255,0.72)",
    },
    /**
     * The confirm button of a delete or remove confirmation: the tonal
     * shape, tinted with `text.destructive` and labelled in it.
     */
    destructive: {
      background: withAlpha(colors.danger, 0.1),
      pressedBackground: withAlpha(colors.danger, 0.16),
      foreground: colors.danger,
    },
  },
  overlay: {
    scrim: "rgba(0, 0, 0, 0.5)",
    lightScrim: "rgba(0, 0, 0, 0.3)",
    pressed: "rgba(42, 37, 34, 0.06)",
    hover: "rgba(42, 37, 34, 0.04)",
  },
  /**
   * Status colours (#3910). Words and icons take the role colour; its tint
   * sits behind a tag or dot. Warning words take `warningText`, because the
   * yellow does not read as words on cream. Info has no colour of its own.
   */
  status: {
    success: colors.success,
    successTint: colors.successTint,
    warning: colors.warning,
    warningText: colors.warningText,
    warningTint: colors.warningTint,
    danger: colors.danger,
    dangerTint: colors.dangerTint,
    info: colors.blue,
    neutral: colors.softBrownLight,
    /** The confirmation toast's layer over the velvet texture. */
    successLayer: withAlpha(colors.success, 0.62),
  },
  /** A logged dose's mark, everywhere a dose is shown. */
  doseStatus: {
    taken: colors.success,
    late: colors.warning,
    missed: colors.danger,
    skipped: colors.softBrownLight,
  },
  /** Charts keep their own palettes (#3910). */
  chart: {
    /** The embryo chromosome chart's marks and legend. */
    chromosome: {
      mosaic: "#B08030",
      different: "#A8563A",
    },
  },
  /**
   * The quiet neutral wash behind notices and disclaimers. It carries no tone:
   * notices blend into the surface, and an error is marked by its icon.
   */
  notice: {
    quiet: {
      background: "rgba(42, 37, 34, 0.04)",
    },
  },
  /**
   * Words and fills on a dark card or texture (the Hero, the cycle heroes,
   * the Care current cycle and cryo vault cards, the dark logging panel, the
   * velvet dock, the sign-in glass). White is the base, at three word levels
   * and two fill levels. No word goes fainter than `tertiary`: below it, words
   * stop reading on the copper glow (#3910, colours decision 3).
   */
  onDark: {
    text: {
      /** Titles, key numbers, and the labels of buttons and choices. */
      primary: "#FFFFFF",
      /** Supporting lines under a title, and tag text. */
      secondary: "rgba(255, 255, 255, 0.72)",
      /** Eyebrows, small labels over a value, meta, captions, placeholders. */
      tertiary: "rgba(255, 255, 255, 0.56)",
    },
    /** The cycle detail hero's title, which keeps its warm cream. */
    titleWarm: "#FDDAB0",
    /**
     * The cycle detail hero's eyebrow and phase tag: a light coral that reads
     * on the bronze, kept as approved with the white levels (#3910).
     */
    accent: "#E8875C",
    fill: {
      /** A faint wash: glass chips, inputs, the panel's fill. */
      subtle: "rgba(255, 255, 255, 0.1)",
      /** A stronger wash or a pressed one, and the panel's hairline. */
      strong: "rgba(255, 255, 255, 0.16)",
    },
    /** The error red lifted so it reads on dark glass. */
    danger: "#F2A48E",
  },
} as const;

// ─── Gradients ───────────────────────────────────────────────────────

/** Gradient fills, ready to spread onto a LinearGradient. */
export const gradients = {
  /** The accent fill, lit from the top left like metal (#3910). */
  accent: {
    colors: colors.accentGradient,
    start: { x: 0, y: 0 },
    end: { x: 1, y: 1 },
  },
} as const;

// ─── Typography ──────────────────────────────────────────────────────

export const typography = {
  families: {
    serif: "InstrumentSerif_400Regular",
    sans: "DMSans_400Regular",
    sansMedium: "DMSans_500Medium",
    sansBold: "DMSans_700Bold",
    mono: "DMMono_400Regular",
  },
  sizes: {
    xs: 10,
    sm: 11,
    base: 13,
    md: 14,
    lg: 16,
    xl: 20,
    "2xl": 24,
    "3xl": 28,
    "4xl": 32,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.4,
    relaxed: 1.55,
  },
} as const;

// ─── Spacing (8-point grid) ──────────────────────────────────────────

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 40,
  "5xl": 48,
} as const;

// ─── Border Radii ────────────────────────────────────────────────────

export const radii = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 999,
} as const;

// ─── Shadows ─────────────────────────────────────────────────────────

export const shadows = {
  sm: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 1,
  },
  md: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  lg: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 4,
  },
} as const;

// ─── Spring Configs ─────────────────────────────────────────────────

export const springs = {
  /** Modal/sheet entrance & exit */
  sheet: { damping: 20, stiffness: 200, mass: 0.8 },
  /** Pressable press-in / release */
  press: { damping: 15, stiffness: 300 },
  /** Toggle knob spring */
  toggle: { damping: 15, stiffness: 220, mass: 0.6 },
  /** Playful bounce (badges, celebrations) */
  bounce: { damping: 12, stiffness: 180, mass: 0.6 },
} as const;

// ─── Layout Constants ────────────────────────────────────────────────

export const HEADER_INSET = 34;
export const SCREEN_PADDING_TOP = Platform.OS === "web" ? HEADER_INSET : 0;

// ─── Card Surface Style ──────────────────────────────────────────────

export const cardSurfaceStyle = {
  borderRadius: 16,
  backgroundColor: semanticColors.surface.glass,
  borderWidth: 1,
  borderColor: semanticColors.border.glass,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.08,
  shadowRadius: 32,
  ...(Platform.OS === "web" && {
    backdropFilter: "blur(60px)",
    WebkitBackdropFilter: "blur(60px)",
  }),
} as Record<string, unknown>;
